import type {
  Game,
  GameResult,
  GameRoster,
  GoalieStat,
  Id,
  RosterEntry,
  ScoreEvent,
  StandingsRecord,
  Team,
  Tournament,
} from '../models';
import {
  computeGoalieStats,
  findGameWinnerEventId,
  isShootoutMade,
  resolvePeriodLengthMinutes,
} from './scoringEngine';
import type { AppDatabase } from './store';

type FormResult = 'W' | 'L' | 'T';

export interface AnalyticsPeriodBreakdown {
  period: number;
  goals: number;
  assists: number;
  shots: number;
  penaltyMinutes: number;
  powerPlayGoals: number;
  shortHandedGoals: number;
}

export interface PlayerGameLogEntry {
  gameId: Id;
  date?: string;
  teamId: Id;
  opponentTeamId: Id;
  result: FormResult;
  goals: number;
  assists: number;
  points: number;
  shots: number;
  penaltyMinutes: number;
  shootoutAttempts: number;
  shootoutGoals: number;
}

export interface StreakSummary {
  currentPointStreak: number;
  longestPointStreak: number;
  currentGoalStreak: number;
  longestGoalStreak: number;
  currentPointDrought: number;
}

export interface AssistChemistryRow {
  playerId: Id;
  playerName: string;
  goalsAssistedByPlayer: number;
  assistsOnPlayerGoals: number;
  totalCombinations: number;
}

export interface PlayerAnalyticsSummary {
  playerId: Id;
  profileId?: Id;
  playerName: string;
  teamIds: Id[];
  teamNames: string[];
  gamesPlayed: number;
  goals: number;
  assists: number;
  points: number;
  shots: number;
  penaltyMinutes: number;
  powerPlayGoals: number;
  shortHandedGoals: number;
  gameWinningGoals: number;
  shootoutAttempts: number;
  shootoutGoals: number;
  shootoutPct: number;
  hatTricks: number;
  multiGoalGames: number;
  multiPointGames: number;
  gamesWithPoint: number;
  gamesWithoutPoint: number;
  bestGame?: PlayerGameLogEntry;
  streaks: StreakSummary;
  scoringByPeriod: AnalyticsPeriodBreakdown[];
  lateGoals: number;
  clutchGoals: number;
  tyingGoals: number;
  goAheadGoals: number;
  insuranceGoals: number;
  comebackGoals: number;
  assistChemistry: AssistChemistryRow[];
  gameLog: PlayerGameLogEntry[];
}

export interface TeamPeriodBreakdown {
  period: number;
  goalsFor: number;
  goalsAgainst: number;
  shotsFor: number;
  shotsAgainst: number;
  penaltyMinutes: number;
  powerPlayGoals: number;
  shortHandedGoals: number;
}

export interface TeamHeadToHeadRow {
  opponentTeamId: Id;
  opponentName: string;
  gamesPlayed: number;
  wins: number;
  losses: number;
  ties: number;
  goalsFor: number;
  goalsAgainst: number;
}

export interface TeamPlayerContribution {
  playerId: Id;
  playerName: string;
  goals: number;
  assists: number;
  points: number;
  shots: number;
  penaltyMinutes: number;
}

export interface TeamAnalyticsSummary {
  teamId: Id;
  teamName: string;
  divisionId: Id;
  gamesPlayed: number;
  wins: number;
  losses: number;
  ties: number;
  points: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifferential: number;
  goalsForPerGame: number;
  goalsAgainstPerGame: number;
  penaltyMinutes: number;
  currentStreak: string;
  longestWinStreak: number;
  longestPointStreak: number;
  recentForm: FormResult[];
  comebackWins: number;
  shutouts: number;
  oneGoalGames: number;
  overtimeWins: number;
  overtimeLosses: number;
  shootoutWins: number;
  shootoutLosses: number;
  scoringByPeriod: TeamPeriodBreakdown[];
  powerPlayGoals: number;
  shortHandedGoals: number;
  topPlayers: TeamPlayerContribution[];
  headToHead: TeamHeadToHeadRow[];
}

export interface GoalieAnalyticsSummary extends GoalieStat {
  playerName: string;
  teamName?: string;
}

export interface DeterministicEventAnalytics {
  generatedAt: string;
  tournament: Pick<Tournament, 'id' | 'name' | 'slug' | 'format' | 'status'>;
  players: PlayerAnalyticsSummary[];
  teams: TeamAnalyticsSummary[];
  goalies: GoalieAnalyticsSummary[];
}

interface EventContext {
  tournament: Tournament;
  games: Game[];
  finalGames: Game[];
  resultsByGameId: Map<Id, GameResult>;
  eventsByGameId: Map<Id, ScoreEvent[]>;
  rosterEntries: RosterEntry[];
  gameRosters: GameRoster[];
  teamById: Map<Id, Team>;
  scheduleStartById: Map<Id, string>;
  standingsByTeamId: Map<Id, StandingsRecord>;
  periodLengthMinutes: number;
  regulationPeriodCount: number;
}

interface GameGoalContext {
  eventId: Id;
  teamId?: Id;
  playerId?: Id;
  period: number;
  gameSecond?: number;
  isGameWinner: boolean;
  isPowerPlay: boolean;
  isShortHanded: boolean;
  tying: boolean;
  goAhead: boolean;
  insurance: boolean;
  comeback: boolean;
  late: boolean;
  clutch: boolean;
}

export function buildDeterministicEventAnalytics(
  db: AppDatabase,
  tournamentIdOrSlug: Id,
): DeterministicEventAnalytics | undefined {
  const context = buildContext(db, tournamentIdOrSlug);
  if (!context) return undefined;
  return {
    generatedAt: new Date().toISOString(),
    tournament: {
      id: context.tournament.id,
      name: context.tournament.name,
      slug: context.tournament.slug,
      format: context.tournament.format,
      status: context.tournament.status,
    },
    players: buildPlayerAnalytics(db, context),
    teams: buildTeamAnalytics(db, context),
    goalies: buildGoalieAnalytics(db, context),
  };
}

export function buildPlayerAnalyticsProfiles(
  db: AppDatabase,
  tournamentIdOrSlug: Id,
): PlayerAnalyticsSummary[] {
  const context = buildContext(db, tournamentIdOrSlug);
  return context ? buildPlayerAnalytics(db, context) : [];
}

export function buildTeamAnalyticsProfiles(
  db: AppDatabase,
  tournamentIdOrSlug: Id,
): TeamAnalyticsSummary[] {
  const context = buildContext(db, tournamentIdOrSlug);
  return context ? buildTeamAnalytics(db, context) : [];
}

function buildContext(db: AppDatabase, tournamentIdOrSlug: Id): EventContext | undefined {
  const tournament = db.tournaments.find((row) => row.id === tournamentIdOrSlug || row.slug === tournamentIdOrSlug);
  if (!tournament) return undefined;
  const games = db.games.filter((row) => row.tournamentId === tournament.id);
  const finalGames = games.filter((row) => row.status === 'final' || row.status === 'forfeit');
  const gameIds = new Set(games.map((row) => row.id));
  const resultsByGameId = new Map(
    db.gameResults.filter((row) => gameIds.has(row.gameId)).map((row) => [row.gameId, row]),
  );
  const eventsByGameId = new Map<Id, ScoreEvent[]>();
  for (const event of db.scoreEvents.filter((row) => gameIds.has(row.gameId))) {
    const list = eventsByGameId.get(event.gameId) ?? [];
    list.push(event);
    eventsByGameId.set(event.gameId, list);
  }
  for (const list of eventsByGameId.values()) list.sort(compareScoreEvents);
  return {
    tournament,
    games,
    finalGames,
    resultsByGameId,
    eventsByGameId,
    rosterEntries: db.rosterEntries.filter((row) => row.tournamentId === tournament.id),
    gameRosters: db.gameRosters.filter((row) => gameIds.has(row.gameId)),
    teamById: new Map(db.teams.filter((row) => row.tournamentId === tournament.id).map((row) => [row.id, row])),
    scheduleStartById: new Map(db.scheduleSlots.map((row) => [row.id, row.startsAt])),
    standingsByTeamId: new Map(db.standings.filter((row) => row.tournamentId === tournament.id).map((row) => [row.teamId, row])),
    periodLengthMinutes: resolvePeriodLengthMinutes(tournament.settings.scoring),
    regulationPeriodCount: tournament.settings.scoring.periodCount ?? 3,
  };
}

function buildPlayerAnalytics(db: AppDatabase, context: EventContext): PlayerAnalyticsSummary[] {
  const rosterByPlayerId = new Map<Id, RosterEntry[]>();
  for (const entry of context.rosterEntries) {
    const list = rosterByPlayerId.get(entry.playerId) ?? [];
    list.push(entry);
    rosterByPlayerId.set(entry.playerId, list);
  }
  const playerIds = new Set<Id>([
    ...context.rosterEntries.map((entry) => entry.playerId),
    ...Array.from(context.eventsByGameId.values()).flatMap((events) => events.map((event) => event.playerId).filter(Boolean) as Id[]),
  ]);
  const goalContextByGameId = new Map<Id, GameGoalContext[]>();
  for (const game of context.games) {
    goalContextByGameId.set(game.id, buildGoalContexts(game, context));
  }

  return Array.from(playerIds)
    .map((playerId) => buildOnePlayerAnalytics(db, context, playerId, rosterByPlayerId, goalContextByGameId))
    .sort((a, b) => b.points - a.points || b.goals - a.goals || a.playerName.localeCompare(b.playerName));
}

function buildOnePlayerAnalytics(
  db: AppDatabase,
  context: EventContext,
  playerId: Id,
  rosterByPlayerId: Map<Id, RosterEntry[]>,
  goalContextByGameId: Map<Id, GameGoalContext[]>,
): PlayerAnalyticsSummary {
  const player = db.players.find((row) => row.id === playerId);
  const profile = player ? db.profiles.find((row) => row.id === player.profileId) : undefined;
  const rosterEntries = rosterByPlayerId.get(playerId) ?? [];
  const teamIds = unique(rosterEntries.map((entry) => entry.teamId));
  const teamNames = teamIds.map((teamId) => context.teamById.get(teamId)?.name ?? 'Team');
  const gameLog = buildPlayerGameLog(context, playerId, new Set(teamIds));
  const periodRows = new Map<number, AnalyticsPeriodBreakdown>();
  const chemistry = new Map<Id, AssistChemistryRow>();
  let goals = 0;
  let assists = 0;
  let shots = 0;
  let penaltyMinutes = 0;
  let powerPlayGoals = 0;
  let shortHandedGoals = 0;
  let gameWinningGoals = 0;
  let shootoutAttempts = 0;
  let shootoutGoals = 0;
  let lateGoals = 0;
  let clutchGoals = 0;
  let tyingGoals = 0;
  let goAheadGoals = 0;
  let insuranceGoals = 0;
  let comebackGoals = 0;

  for (const [gameId, events] of context.eventsByGameId.entries()) {
    const goalContexts = goalContextByGameId.get(gameId) ?? [];
    for (const event of events) {
      if (event.playerId === playerId) {
        const period = event.period ?? 1;
        const periodRow = ensurePlayerPeriod(periodRows, period);
        if (isGoalEvent(event)) {
          goals += 1;
          periodRow.goals += 1;
          if (event.isPowerPlay) {
            powerPlayGoals += 1;
            periodRow.powerPlayGoals += 1;
          }
          if (event.isShortHanded) {
            shortHandedGoals += 1;
            periodRow.shortHandedGoals += 1;
          }
          const contextRow = goalContexts.find((row) => row.eventId === event.id);
          if (contextRow?.isGameWinner) gameWinningGoals += 1;
          if (contextRow?.late) lateGoals += 1;
          if (contextRow?.clutch) clutchGoals += 1;
          if (contextRow?.tying) tyingGoals += 1;
          if (contextRow?.goAhead) goAheadGoals += 1;
          if (contextRow?.insurance) insuranceGoals += 1;
          if (contextRow?.comeback) comebackGoals += 1;
        } else if (event.type === 'assist') {
          assists += 1;
          periodRow.assists += 1;
          const parent = event.parentEventId ? events.find((row) => row.id === event.parentEventId) : undefined;
          if (parent?.playerId) {
            const row = ensureChemistry(db, chemistry, parent.playerId);
            row.assistsOnPlayerGoals += 1;
            row.totalCombinations += 1;
          }
        } else if (event.type === 'shot_on_goal') {
          shots += 1;
          periodRow.shots += 1;
        } else if (event.type === 'penalty') {
          const minutes = event.penaltyMinutes ?? 2;
          penaltyMinutes += minutes;
          periodRow.penaltyMinutes += minutes;
        } else if (event.type === 'shootout_attempt') {
          shootoutAttempts += 1;
          if (isShootoutMade(event)) shootoutGoals += 1;
        }
      }
      if (isGoalEvent(event) && event.playerId === playerId) {
        const assistsForGoal = events.filter((row) => row.type === 'assist' && row.parentEventId === event.id && row.playerId);
        for (const assist of assistsForGoal) {
          if (!assist.playerId) continue;
          const row = ensureChemistry(db, chemistry, assist.playerId);
          row.goalsAssistedByPlayer += 1;
          row.totalCombinations += 1;
        }
      }
    }
  }

  const points = goals + assists;
  const bestGame = [...gameLog].sort((a, b) => b.points - a.points || b.goals - a.goals || b.shots - a.shots)[0];
  return {
    playerId,
    profileId: player?.profileId,
    playerName: profile?.displayName ?? 'Unknown player',
    teamIds,
    teamNames,
    gamesPlayed: gameLog.length,
    goals,
    assists,
    points,
    shots,
    penaltyMinutes,
    powerPlayGoals,
    shortHandedGoals,
    gameWinningGoals,
    shootoutAttempts,
    shootoutGoals,
    shootoutPct: shootoutAttempts > 0 ? shootoutGoals / shootoutAttempts : 0,
    hatTricks: gameLog.filter((row) => row.goals >= 3).length,
    multiGoalGames: gameLog.filter((row) => row.goals >= 2).length,
    multiPointGames: gameLog.filter((row) => row.points >= 2).length,
    gamesWithPoint: gameLog.filter((row) => row.points > 0).length,
    gamesWithoutPoint: gameLog.filter((row) => row.points === 0).length,
    bestGame,
    streaks: buildPlayerStreaks(gameLog),
    scoringByPeriod: Array.from(periodRows.values()).sort((a, b) => a.period - b.period),
    lateGoals,
    clutchGoals,
    tyingGoals,
    goAheadGoals,
    insuranceGoals,
    comebackGoals,
    assistChemistry: Array.from(chemistry.values()).sort((a, b) => b.totalCombinations - a.totalCombinations),
    gameLog,
  };
}

function buildPlayerGameLog(context: EventContext, playerId: Id, teamIds: Set<Id>): PlayerGameLogEntry[] {
  const entries: PlayerGameLogEntry[] = [];
  for (const game of sortGamesChronologically(context.finalGames, context)) {
    const playerTeamId = [game.homeTeamId, game.awayTeamId].find((teamId) => teamIds.has(teamId));
    if (!playerTeamId) continue;
    const gameRoster = findGameRoster(context, game.id, playerTeamId);
    const attendance = gameRoster?.attendanceByPlayerId ?? {};
    if (Object.keys(attendance).length > 0 && attendance[playerId] === 'absent') continue;
    const result = context.resultsByGameId.get(game.id);
    if (!result) continue;
    const events = context.eventsByGameId.get(game.id) ?? [];
    const rowEvents = events.filter((event) => event.playerId === playerId);
    const goals = rowEvents.filter(isGoalEvent).length;
    const assists = rowEvents.filter((event) => event.type === 'assist').length;
    const shots = rowEvents.filter((event) => event.type === 'shot_on_goal').length;
    const penaltyMinutes = rowEvents
      .filter((event) => event.type === 'penalty')
      .reduce((sum, event) => sum + (event.penaltyMinutes ?? 2), 0);
    const shootoutAttempts = rowEvents.filter((event) => event.type === 'shootout_attempt').length;
    const shootoutGoals = rowEvents.filter((event) => event.type === 'shootout_attempt' && isShootoutMade(event)).length;
    entries.push({
      gameId: game.id,
      date: gameDate(game, context),
      teamId: playerTeamId,
      opponentTeamId: playerTeamId === game.homeTeamId ? game.awayTeamId : game.homeTeamId,
      result: resultForTeam(playerTeamId, game, result),
      goals,
      assists,
      points: goals + assists,
      shots,
      penaltyMinutes,
      shootoutAttempts,
      shootoutGoals,
    });
  }
  return entries;
}

function buildTeamAnalytics(db: AppDatabase, context: EventContext): TeamAnalyticsSummary[] {
  return Array.from(context.teamById.values())
    .map((team) => buildOneTeamAnalytics(db, context, team))
    .sort((a, b) => b.points - a.points || b.goalDifferential - a.goalDifferential || a.teamName.localeCompare(b.teamName));
}

function buildOneTeamAnalytics(db: AppDatabase, context: EventContext, team: Team): TeamAnalyticsSummary {
  const standing = context.standingsByTeamId.get(team.id);
  const teamGames = sortGamesChronologically(
    context.finalGames.filter((game) => game.homeTeamId === team.id || game.awayTeamId === team.id),
    context,
  );
  const form = teamGames.map((game) => resultForTeam(team.id, game, context.resultsByGameId.get(game.id)));
  const scoringByPeriod = buildTeamPeriodBreakdown(context, team.id);
  const topPlayers = buildTeamPlayerContributions(db, context, team.id);
  const penaltyMinutes = scoringByPeriod.reduce((sum, row) => sum + row.penaltyMinutes, 0);
  const headToHead = buildHeadToHead(context, team.id);
  const gamesPlayed = standing?.gamesPlayed ?? teamGames.length;
  const goalsFor = standing?.goalsFor ?? teamGames.reduce((sum, game) => sum + teamScore(team.id, game, context.resultsByGameId.get(game.id)), 0);
  const goalsAgainst = standing?.goalsAgainst ?? teamGames.reduce((sum, game) => sum + opponentScore(team.id, game, context.resultsByGameId.get(game.id)), 0);
  return {
    teamId: team.id,
    teamName: team.name,
    divisionId: team.divisionId,
    gamesPlayed,
    wins: standing?.wins ?? form.filter((row) => row === 'W').length,
    losses: standing?.losses ?? form.filter((row) => row === 'L').length,
    ties: standing?.ties ?? form.filter((row) => row === 'T').length,
    points: standing?.points ?? 0,
    goalsFor,
    goalsAgainst,
    goalDifferential: goalsFor - goalsAgainst,
    goalsForPerGame: gamesPlayed > 0 ? goalsFor / gamesPlayed : 0,
    goalsAgainstPerGame: gamesPlayed > 0 ? goalsAgainst / gamesPlayed : 0,
    penaltyMinutes,
    currentStreak: formatCurrentStreak(form),
    longestWinStreak: longestResultStreak(form, 'W'),
    longestPointStreak: longestPointStreak(form),
    recentForm: [...form].slice(-5).reverse(),
    comebackWins: teamGames.filter((game) => teamWonComeback(team.id, game, context)).length,
    shutouts: teamGames.filter((game) => {
      const result = context.resultsByGameId.get(game.id);
      return resultForTeam(team.id, game, result) === 'W' && opponentScore(team.id, game, result) === 0;
    }).length,
    oneGoalGames: teamGames.filter((game) => {
      const result = context.resultsByGameId.get(game.id);
      return Math.abs(teamScore(team.id, game, result) - opponentScore(team.id, game, result)) === 1;
    }).length,
    overtimeWins: teamGames.filter((game) => context.resultsByGameId.get(game.id)?.decidedBy === 'overtime' && resultForTeam(team.id, game, context.resultsByGameId.get(game.id)) === 'W').length,
    overtimeLosses: teamGames.filter((game) => context.resultsByGameId.get(game.id)?.decidedBy === 'overtime' && resultForTeam(team.id, game, context.resultsByGameId.get(game.id)) === 'L').length,
    shootoutWins: standing?.shootoutWins ?? teamGames.filter((game) => context.resultsByGameId.get(game.id)?.decidedBy === 'shootout' && resultForTeam(team.id, game, context.resultsByGameId.get(game.id)) === 'W').length,
    shootoutLosses: standing?.shootoutLosses ?? teamGames.filter((game) => context.resultsByGameId.get(game.id)?.decidedBy === 'shootout' && resultForTeam(team.id, game, context.resultsByGameId.get(game.id)) === 'L').length,
    scoringByPeriod,
    powerPlayGoals: scoringByPeriod.reduce((sum, row) => sum + row.powerPlayGoals, 0),
    shortHandedGoals: scoringByPeriod.reduce((sum, row) => sum + row.shortHandedGoals, 0),
    topPlayers,
    headToHead,
  };
}

function buildGoalieAnalytics(db: AppDatabase, context: EventContext): GoalieAnalyticsSummary[] {
  const gameIds = new Set(context.games.map((game) => game.id));
  return computeGoalieStats({
    games: context.games,
    events: Array.from(context.eventsByGameId.values()).flat(),
    results: db.gameResults.filter((result) => gameIds.has(result.gameId)),
    rosterEntries: context.rosterEntries.map((entry) => ({
      teamId: entry.teamId,
      playerId: entry.playerId,
      position: entry.position,
    })),
  }).map((row) => {
    const player = db.players.find((item) => item.id === row.playerId);
    const profile = player ? db.profiles.find((item) => item.id === player.profileId) : undefined;
    return {
      ...row,
      playerName: profile?.displayName ?? 'Unknown goalie',
      teamName: context.teamById.get(row.teamId)?.name,
    };
  });
}

function buildGoalContexts(game: Game, context: EventContext): GameGoalContext[] {
  const events = context.eventsByGameId.get(game.id) ?? [];
  const result = context.resultsByGameId.get(game.id);
  const gameWinnerEventId = result ? findGameWinnerEventId(events, game, result) : undefined;
  const goals = events.filter(isGoalEvent).sort(compareScoreEvents);
  let homeScore = 0;
  let awayScore = 0;
  const regulationSeconds = context.regulationPeriodCount * context.periodLengthMinutes * 60;
  return goals.map((event) => {
    const beforeHome = homeScore;
    const beforeAway = awayScore;
    if (event.teamId === game.homeTeamId) homeScore += 1;
    if (event.teamId === game.awayTeamId) awayScore += 1;
    const beforeFor = event.teamId === game.homeTeamId ? beforeHome : beforeAway;
    const beforeAgainst = event.teamId === game.homeTeamId ? beforeAway : beforeHome;
    const afterFor = event.teamId === game.homeTeamId ? homeScore : awayScore;
    const afterAgainst = event.teamId === game.homeTeamId ? awayScore : homeScore;
    const late = typeof event.gameSecond === 'number' && event.gameSecond >= regulationSeconds - 120;
    const tying = afterFor === afterAgainst && beforeFor < beforeAgainst;
    const goAhead = afterFor > afterAgainst && beforeFor <= beforeAgainst;
    const row: GameGoalContext = {
      eventId: event.id,
      teamId: event.teamId,
      playerId: event.playerId,
      period: event.period ?? 1,
      gameSecond: event.gameSecond,
      isGameWinner: event.isGameWinner === true || event.id === gameWinnerEventId,
      isPowerPlay: event.isPowerPlay === true,
      isShortHanded: event.isShortHanded === true,
      tying,
      goAhead,
      insurance: beforeFor > beforeAgainst && afterFor > afterAgainst,
      comeback: beforeFor < beforeAgainst,
      late,
      clutch: late && (tying || goAhead || event.id === gameWinnerEventId || event.isGameWinner === true),
    };
    return row;
  });
}

function buildTeamPeriodBreakdown(context: EventContext, teamId: Id): TeamPeriodBreakdown[] {
  const periods = new Map<number, TeamPeriodBreakdown>();
  const ensure = (period: number): TeamPeriodBreakdown => {
    const safePeriod = Math.max(1, Number.isFinite(period) ? Math.floor(period) : 1);
    const existing = periods.get(safePeriod);
    if (existing) return existing;
    const row: TeamPeriodBreakdown = {
      period: safePeriod,
      goalsFor: 0,
      goalsAgainst: 0,
      shotsFor: 0,
      shotsAgainst: 0,
      penaltyMinutes: 0,
      powerPlayGoals: 0,
      shortHandedGoals: 0,
    };
    periods.set(safePeriod, row);
    return row;
  };
  for (let period = 1; period <= context.regulationPeriodCount; period += 1) ensure(period);
  for (const game of context.games.filter((row) => row.homeTeamId === teamId || row.awayTeamId === teamId)) {
    const opponentId = game.homeTeamId === teamId ? game.awayTeamId : game.homeTeamId;
    for (const event of context.eventsByGameId.get(game.id) ?? []) {
      const row = ensure(event.period ?? 1);
      if (event.teamId === teamId) {
        if (isGoalEvent(event)) row.goalsFor += 1;
        if (event.type === 'shot_on_goal') row.shotsFor += 1;
        if (event.type === 'penalty') row.penaltyMinutes += event.penaltyMinutes ?? 2;
        if (event.type === 'goal' && event.isPowerPlay) row.powerPlayGoals += 1;
        if (event.type === 'goal' && event.isShortHanded) row.shortHandedGoals += 1;
      } else if (event.teamId === opponentId) {
        if (isGoalEvent(event)) row.goalsAgainst += 1;
        if (event.type === 'shot_on_goal') row.shotsAgainst += 1;
      }
    }
  }
  return Array.from(periods.values()).sort((a, b) => a.period - b.period);
}

function buildTeamPlayerContributions(db: AppDatabase, context: EventContext, teamId: Id): TeamPlayerContribution[] {
  const rows = new Map<Id, TeamPlayerContribution>();
  const ensure = (playerId: Id): TeamPlayerContribution => {
    const existing = rows.get(playerId);
    if (existing) return existing;
    const player = db.players.find((row) => row.id === playerId);
    const profile = player ? db.profiles.find((row) => row.id === player.profileId) : undefined;
    const row: TeamPlayerContribution = {
      playerId,
      playerName: profile?.displayName ?? 'Unknown player',
      goals: 0,
      assists: 0,
      points: 0,
      shots: 0,
      penaltyMinutes: 0,
    };
    rows.set(playerId, row);
    return row;
  };
  for (const game of context.games.filter((row) => row.homeTeamId === teamId || row.awayTeamId === teamId)) {
    for (const event of context.eventsByGameId.get(game.id) ?? []) {
      if (!event.playerId || event.teamId !== teamId) continue;
      const row = ensure(event.playerId);
      if (isGoalEvent(event)) row.goals += 1;
      if (event.type === 'assist') row.assists += 1;
      if (event.type === 'shot_on_goal') row.shots += 1;
      if (event.type === 'penalty') row.penaltyMinutes += event.penaltyMinutes ?? 2;
      row.points = row.goals + row.assists;
    }
  }
  return Array.from(rows.values()).sort((a, b) => b.points - a.points || b.goals - a.goals).slice(0, 10);
}

function buildHeadToHead(context: EventContext, teamId: Id): TeamHeadToHeadRow[] {
  const rows = new Map<Id, TeamHeadToHeadRow>();
  for (const game of context.finalGames.filter((row) => row.homeTeamId === teamId || row.awayTeamId === teamId)) {
    const result = context.resultsByGameId.get(game.id);
    if (!result) continue;
    const opponentTeamId = game.homeTeamId === teamId ? game.awayTeamId : game.homeTeamId;
    const row = rows.get(opponentTeamId) ?? {
      opponentTeamId,
      opponentName: context.teamById.get(opponentTeamId)?.name ?? 'Opponent',
      gamesPlayed: 0,
      wins: 0,
      losses: 0,
      ties: 0,
      goalsFor: 0,
      goalsAgainst: 0,
    };
    row.gamesPlayed += 1;
    const teamGoals = teamScore(teamId, game, result);
    const oppGoals = opponentScore(teamId, game, result);
    row.goalsFor += teamGoals;
    row.goalsAgainst += oppGoals;
    if (teamGoals > oppGoals) row.wins += 1;
    else if (oppGoals > teamGoals) row.losses += 1;
    else row.ties += 1;
    rows.set(opponentTeamId, row);
  }
  return Array.from(rows.values()).sort((a, b) => b.gamesPlayed - a.gamesPlayed || a.opponentName.localeCompare(b.opponentName));
}

function teamWonComeback(teamId: Id, game: Game, context: EventContext): boolean {
  const result = context.resultsByGameId.get(game.id);
  if (!result || result.winningTeamId !== teamId) return false;
  let homeScore = 0;
  let awayScore = 0;
  let trailed = false;
  for (const event of (context.eventsByGameId.get(game.id) ?? []).filter(isGoalEvent).sort(compareScoreEvents)) {
    if (event.teamId === game.homeTeamId) homeScore += 1;
    if (event.teamId === game.awayTeamId) awayScore += 1;
    const teamGoals = teamId === game.homeTeamId ? homeScore : awayScore;
    const oppGoals = teamId === game.homeTeamId ? awayScore : homeScore;
    if (oppGoals > teamGoals) trailed = true;
  }
  return trailed;
}

function buildPlayerStreaks(gameLog: PlayerGameLogEntry[]): StreakSummary {
  let currentPointStreak = 0;
  let currentGoalStreak = 0;
  let currentPointDrought = 0;
  for (let i = gameLog.length - 1; i >= 0; i -= 1) {
    if (gameLog[i].points > 0) currentPointStreak += 1;
    else break;
  }
  for (let i = gameLog.length - 1; i >= 0; i -= 1) {
    if (gameLog[i].goals > 0) currentGoalStreak += 1;
    else break;
  }
  for (let i = gameLog.length - 1; i >= 0; i -= 1) {
    if (gameLog[i].points === 0) currentPointDrought += 1;
    else break;
  }
  return {
    currentPointStreak,
    longestPointStreak: longestByPredicate(gameLog, (row) => row.points > 0),
    currentGoalStreak,
    longestGoalStreak: longestByPredicate(gameLog, (row) => row.goals > 0),
    currentPointDrought,
  };
}

function longestByPredicate<T>(rows: T[], predicate: (row: T) => boolean): number {
  let longest = 0;
  let current = 0;
  for (const row of rows) {
    if (predicate(row)) {
      current += 1;
      longest = Math.max(longest, current);
    } else {
      current = 0;
    }
  }
  return longest;
}

function longestResultStreak(form: FormResult[], result: FormResult): number {
  return longestByPredicate(form, (row) => row === result);
}

function longestPointStreak(form: FormResult[]): number {
  return longestByPredicate(form, (row) => row === 'W' || row === 'T');
}

function formatCurrentStreak(form: FormResult[]): string {
  const latest = form[form.length - 1];
  if (!latest) return '0';
  let count = 0;
  for (let i = form.length - 1; i >= 0; i -= 1) {
    if (form[i] !== latest) break;
    count += 1;
  }
  return `${latest}${count}`;
}

function ensurePlayerPeriod(rows: Map<number, AnalyticsPeriodBreakdown>, period: number): AnalyticsPeriodBreakdown {
  const safePeriod = Math.max(1, Number.isFinite(period) ? Math.floor(period) : 1);
  const existing = rows.get(safePeriod);
  if (existing) return existing;
  const row: AnalyticsPeriodBreakdown = {
    period: safePeriod,
    goals: 0,
    assists: 0,
    shots: 0,
    penaltyMinutes: 0,
    powerPlayGoals: 0,
    shortHandedGoals: 0,
  };
  rows.set(safePeriod, row);
  return row;
}

function ensureChemistry(db: AppDatabase, rows: Map<Id, AssistChemistryRow>, playerId: Id): AssistChemistryRow {
  const existing = rows.get(playerId);
  if (existing) return existing;
  const player = db.players.find((row) => row.id === playerId);
  const profile = player ? db.profiles.find((row) => row.id === player.profileId) : undefined;
  const row: AssistChemistryRow = {
    playerId,
    playerName: profile?.displayName ?? 'Unknown player',
    goalsAssistedByPlayer: 0,
    assistsOnPlayerGoals: 0,
    totalCombinations: 0,
  };
  rows.set(playerId, row);
  return row;
}

function isGoalEvent(event: ScoreEvent): boolean {
  return event.type === 'goal' || event.type === 'penalty_shot_made';
}

function compareScoreEvents(a: ScoreEvent, b: ScoreEvent): number {
  const aSecond = Number.isFinite(a.gameSecond) ? a.gameSecond! : Number.POSITIVE_INFINITY;
  const bSecond = Number.isFinite(b.gameSecond) ? b.gameSecond! : Number.POSITIVE_INFINITY;
  if (aSecond !== bSecond) return aSecond - bSecond;
  return a.recordedAt.localeCompare(b.recordedAt);
}

function sortGamesChronologically(games: Game[], context: EventContext): Game[] {
  return [...games].sort((a, b) => gameDate(a, context).localeCompare(gameDate(b, context)) || a.id.localeCompare(b.id));
}

function gameDate(game: Game, context: EventContext): string {
  const result = context.resultsByGameId.get(game.id);
  if (result?.endedAt) return result.endedAt;
  return (game.scheduleSlotId ? context.scheduleStartById.get(game.scheduleSlotId) : undefined) ?? game.updatedAt ?? game.createdAt;
}

function findGameRoster(context: EventContext, gameId: Id, teamId: Id): GameRoster | undefined {
  return context.gameRosters.find((row) => row.gameId === gameId && row.teamId === teamId);
}

function resultForTeam(teamId: Id, game: Game, result: GameResult | undefined): FormResult {
  const teamGoals = teamScore(teamId, game, result);
  const oppGoals = opponentScore(teamId, game, result);
  if (teamGoals === oppGoals) return 'T';
  return teamGoals > oppGoals ? 'W' : 'L';
}

function teamScore(teamId: Id, game: Game, result: GameResult | undefined): number {
  if (!result) return 0;
  return teamId === game.homeTeamId ? result.homeScore : result.awayScore;
}

function opponentScore(teamId: Id, game: Game, result: GameResult | undefined): number {
  if (!result) return 0;
  return teamId === game.homeTeamId ? result.awayScore : result.homeScore;
}

function unique<T>(rows: T[]): T[] {
  return Array.from(new Set(rows));
}
